import React, { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Mail, Instagram, Youtube, Linkedin, ArrowUpRight } from "lucide-react";

const NAV_ITEMS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/work", label: "Work" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed top-0 left-0 right-0 z-[60] transition-all duration-500 ${
          scrolled
            ? "bg-ink/85 backdrop-blur-md border-b border-gold/20"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="mx-auto max-w-[1500px] px-6 md:px-12 flex items-center justify-between h-20 md:h-24">
          {/* Logo */}
          <Link to="/" data-testid="nav-logo" className="flex items-center group">
            <img
              src="/brand/cineprompt-logo.png"
              alt="Cine Prompt"
              className="h-14 md:h-16 w-auto transition-transform duration-500 group-hover:scale-[1.04]"
              width="200"
              height="200"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-9" data-testid="nav-desktop">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `nav-underline relative text-[11px] tracking-[0.28em] uppercase font-sans transition-colors duration-300 ${
                    isActive ? "text-gold is-active" : "text-bone/75 hover:text-gold"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* Desktop CTA */}
          <Link
            to="/contact"
            data-testid="nav-cta-start"
            className="hidden md:inline-flex cp-btn cp-btn--glow"
          >
            Get In Touch
          </Link>

          {/* Mobile toggle */}
          <button
            className="md:hidden text-bone p-2 relative z-[80]"
            onClick={() => setOpen((s) => !s)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* Full-screen mobile cinematic overlay */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="md:hidden fixed inset-0 z-[70] bg-ink/95 backdrop-blur-xl overflow-y-auto"
            data-testid="nav-mobile-panel"
          >
            {/* Decorative glow blobs */}
            <div
              className="cp-glow-blob cp-glow-blob--gold"
              style={{ top: "-10%", right: "-10%", opacity: 0.25 }}
            />
            <div
              className="cp-glow-blob cp-glow-blob--ember"
              style={{ bottom: "-15%", left: "-15%", opacity: 0.2 }}
            />

            <div className="relative min-h-screen flex flex-col">
              {/* Top bar inside overlay */}
              <div className="px-6 pt-5 pb-2 flex items-center justify-between">
                <span className="font-serif text-bone text-xl tracking-tight">
                  Cine<span className="text-gold">.</span>Prompt
                </span>
                <span className="overline">Menu</span>
              </div>
              <div className="hairline mx-6 mt-3" />

              {/* Links */}
              <nav className="flex-1 px-6 pt-12 pb-8">
                <p className="overline mb-6">Navigate</p>
                <ul className="flex flex-col">
                  {NAV_ITEMS.map((item, i) => (
                    <li key={item.to}>
                      <motion.div
                        initial={{ opacity: 0, y: 22 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.12 + i * 0.06, duration: 0.5, ease: [0.22, 0.65, 0.2, 1] }}
                      >
                        <NavLink
                          to={item.to}
                          data-testid={`nav-mobile-link-${item.label.toLowerCase()}`}
                          className={({ isActive }) =>
                            `group flex items-center justify-between py-5 border-b border-gold/15 ${
                              isActive ? "text-gold" : "text-bone"
                            }`
                          }
                        >
                          <span className="flex items-baseline gap-4">
                            <span className="text-gold/70 font-sans text-[10px] tracking-[0.3em]">
                              {String(i + 1).padStart(2, "0")}
                            </span>
                            <span className="font-serif text-4xl sm:text-5xl tracking-tight">
                              {item.label}
                            </span>
                          </span>
                          <ArrowUpRight
                            size={20}
                            className="text-bone/35 group-hover:text-gold transition-colors -rotate-45"
                          />
                        </NavLink>
                      </motion.div>
                    </li>
                  ))}
                </ul>
              </nav>

              {/* Footer of overlay */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55, duration: 0.6 }}
                className="px-6 pb-10"
              >
                <Link
                  to="/contact"
                  className="cp-btn cp-btn--glow w-full justify-center"
                  data-testid="nav-mobile-cta"
                >
                  Get In Touch <ArrowUpRight size={14} />
                </Link>

                <div className="hairline mt-10 mb-6" />

                <a
                  href="mailto:samresh.shrivastav@gmail.com"
                  className="inline-flex items-center gap-3 text-bone/80 hover:text-gold transition-colors"
                >
                  <Mail size={14} className="text-gold" />
                  <span className="font-serif text-lg">samresh.shrivastav@gmail.com</span>
                </a>

                <ul className="flex items-center gap-3 mt-6">
                  {[
                    { name: "Instagram", Icon: Instagram, href: "#" },
                    { name: "YouTube", Icon: Youtube, href: "https://youtube.com/@apocalypto22" },
                    { name: "LinkedIn", Icon: Linkedin, href: "#" },
                  ].map(({ name, Icon, href }) => {
                    const isExternal = href.startsWith("http");
                    return (
                      <li key={name}>
                        <a
                          href={href}
                          aria-label={name}
                          {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                          className="group inline-flex items-center justify-center w-11 h-11 border border-bone/20 hover:border-gold hover:bg-gold transition-all duration-500"
                        >
                          <Icon
                            size={15}
                            strokeWidth={1.4}
                            className="text-bone/75 group-hover:text-ink transition-colors duration-500"
                          />
                        </a>
                      </li>
                    );
                  })}
                </ul>

                <p className="mt-8 text-bone/45 text-[10px] tracking-[0.3em] uppercase">
                  © 2025 Cine Prompt · Engineered for Cinema
                </p>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
